import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import SoundPackUpload from './admin/SoundPackUpload';
import EditPreview from './admin/EditPreview';
import MakePublic from './admin/PublishToProduction';
import TagCollectionEditor from './admin/TagCollectionEditor';
import EmailWhitelist from './admin/EmailWhitelist';
import NewsEditor from './admin/NewsEditor';
import UpcomingPacksEditor from './admin/UpcomingPacksEditor';
import { firebaseEnv } from '../services/firebase';

function AdminPage() {
    const { isAdmin: isAdminUser, isLoading: isCheckingAdmin } = useUser();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'upload' | 'link' | 'publish' | 'upcoming' | 'news' | 'tags' | 'whitelist'>('upload');

    if (isCheckingAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!isAdminUser) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
                    <p className="text-gray-700 mb-4">You don't have permission to access this page.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tab Navigation */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                onClick={() => setActiveTab('upload')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'upload'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Upload sound pack
                            </button>
                            <button
                                onClick={() => setActiveTab('link')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'link'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Edit preview
                            </button>
                            <button
                                onClick={() => setActiveTab('publish')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'publish'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Make public
                            </button>
                            <button
                                onClick={() => setActiveTab('upcoming')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'upcoming'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Upcoming
                            </button>
                            <button
                                onClick={() => setActiveTab('news')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'news'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                News
                            </button>
                            <button
                                onClick={() => setActiveTab('tags')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'tags'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Tag collection
                            </button>
                            <button
                                onClick={() => setActiveTab('whitelist')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'whitelist'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Email whitelist
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-lg shadow p-6">
                    {activeTab === 'upload' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Upload Sound Pack ({firebaseEnv.environment})</h2>
                            <p className="text-gray-600 mb-4">Upload a new sound pack. It will only be visible to admin accounts, until published.</p>
                            <SoundPackUpload />
                        </div>
                    )}

                    {activeTab === 'link' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Edit Preview Packs</h2>
                            <p className="text-gray-600 mb-4">Edit the name and Stripe price ID of preview sound packs.</p>
                            <EditPreview />
                        </div>
                    )}

                    {activeTab === 'publish' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Make Public</h2>
                            <p className="text-gray-600 mb-4">Make tested sound packs visible to all users by changing their status to "public".</p>
                            <MakePublic />
                        </div>
                    )}

                    {activeTab === 'upcoming' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Upcoming Packs ({firebaseEnv.environment})</h2>
                            <p className="text-gray-600 mb-4">Teasers shown in the plugin's library as "Coming &lt;date&gt;". Private teasers are visible to admin accounts only. To release one, pick it under "Replaces upcoming pack" when uploading the real pack, then delete the teaser here. Teasers are written per environment; nothing is copied to production.</p>
                            <UpcomingPacksEditor />
                        </div>
                    )}

                    {activeTab === 'news' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">News ({firebaseEnv.environment})</h2>
                            <p className="text-gray-600 mb-4">Posts for the plugin's News tab. Non-public entries are visible to admin accounts only — check them in the plugin, then tick Public. Entries are written per environment; nothing is copied to production.</p>
                            <NewsEditor />
                        </div>
                    )}

                    {activeTab === 'tags' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Tag Collection ({firebaseEnv.environment})</h2>
                            <p className="text-gray-600 mb-4">Edit the global tag and instrument-type vocabulary. Ids are permanent slugs (copy them for external tagging tools); publishing warns if any sound still references a removed id.</p>
                            <TagCollectionEditor />
                        </div>
                    )}

                    {activeTab === 'whitelist' && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Email Whitelist ({firebaseEnv.environment})</h2>
                            <p className="text-gray-600 mb-4">Control who can sign in during the closed beta. A verified account whose address isn't on this list is blocked from the website and can't log in from the plugin.</p>
                            <EmailWhitelist />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminPage;
